import React, { useState } from 'react';
import { Modal, Tab, Tabs, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { useAppDispatch } from '../store/hooks';
import { loginSuccess, setAuthError, emailConfirmed } from '../store/authSlice';
import * as authService from '../services/authService';
import { migrateGuestTrackOnLogin } from '../services/trackStore';

// ── Password input with show/hide eye toggle ──────────────────────────────────

interface PasswordInputProps {
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  disabled?: boolean;
  required?: boolean;
}

function PasswordInput({ value, onChange, autoComplete, disabled, required }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  return (
    <InputGroup>
      <Form.Control
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        disabled={disabled}
      />
      <Button
        type="button"
        variant="outline-secondary"
        className="password-toggle"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        aria-label={visible ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        {visible ? (
          // eye-off
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a19.77 19.77 0 0 1 4.06-5.06" />
            <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 7 11 7a19.77 19.77 0 0 1-3.17 4.19" />
            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
            <line x1="2" y1="2" x2="22" y2="22" />
          </svg>
        ) : (
          // eye
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </Button>
    </InputGroup>
  );
}

type ActiveTab = 'login' | 'register' | 'forgot' | 'confirm';

interface Props {
  show: boolean;
  onHide: () => void;
  /** Optional message shown at the top of the modal (e.g. why login is required). */
  message?: string | null;
  /** Which tab/view to show when the modal opens. Defaults to 'login'. */
  initialTab?: ActiveTab;
}

// ── Login form ────────────────────────────────────────────────────────────────

function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const dispatch = useAppDispatch();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authService.login(username.trim(), password);
      await migrateGuestTrackOnLogin();
      dispatch(loginSuccess({ username: res.username ?? username.trim() }));
      onSuccess();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      dispatch(setAuthError(message));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form.Group className="mb-3" controlId="login-username">
        <Form.Label>Username / Email</Form.Label>
        <Form.Control
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          required
          disabled={loading}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="login-password">
        <Form.Label>Password</Form.Label>
        <PasswordInput
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
          disabled={loading}
        />
      </Form.Group>
      <div className="d-grid">
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" className="me-2" /> : null}
          Sign In
        </Button>
      </div>
    </Form>
  );
}

// ── Register form ─────────────────────────────────────────────────────────────

function RegisterForm({ onRegistered }: { onRegistered: (username: string) => void }) {
  const dispatch = useAppDispatch();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await authService.register(username.trim(), email.trim(), password);
      await migrateGuestTrackOnLogin();
      const resolvedUsername = res.username ?? username.trim();
      dispatch(loginSuccess({ username: resolvedUsername, emailConfirmPending: true }));
      onRegistered(resolvedUsername);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      dispatch(setAuthError(message));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form.Group className="mb-3" controlId="reg-username">
        <Form.Label>Username</Form.Label>
        <Form.Control
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          required
          disabled={loading}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="reg-email">
        <Form.Label>Email address</Form.Label>
        <Form.Control
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          disabled={loading}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="reg-password">
        <Form.Label>Password</Form.Label>
        <PasswordInput
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          disabled={loading}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="reg-confirm">
        <Form.Label>Confirm password</Form.Label>
        <PasswordInput
          value={confirmPassword}
          onChange={setConfirmPassword}
          autoComplete="new-password"
          required
          disabled={loading}
        />
      </Form.Group>
      <div className="d-grid">
        <Button type="submit" variant="success" disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" className="me-2" /> : null}
          Create Account
        </Button>
      </div>
    </Form>
  );
}

// ── Forgot-password form ──────────────────────────────────────────────────────

function ForgotPasswordForm({ onBack, onDone }: { onBack: () => void; onDone: () => void }) {
  const [step, setStep] = useState<'request' | 'reset' | 'done'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await authService.forgotPassword(email.trim());
      setStep('reset');
      setInfo(`We've sent a 6-digit code to ${email.trim()}. Enter it below to reset your password.`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Please enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword(email.trim(), code.trim(), password);
      setStep('done');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendCode() {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await authService.forgotPassword(email.trim());
      setInfo(`A new 6-digit code has been sent to ${email.trim()}.`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  if (step === 'done') {
    return (
      <>
        <Alert variant="success">
          Your password has been reset successfully. You can now sign in with your new password.
        </Alert>
        <Button variant="primary" onClick={onDone}>
          Close
        </Button>
      </>
    );
  }

  if (step === 'reset') {
    return (
      <Form onSubmit={handleResetPassword}>
        {info && <Alert variant="info" className="small">{info}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group className="mb-3" controlId="reset-email">
          <Form.Label>Email address</Form.Label>
          <Form.Control
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            disabled={loading}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="reset-code">
          <Form.Label>6-digit code</Form.Label>
          <Form.Control
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            required
            disabled={loading}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="reset-password">
          <Form.Label>New password</Form.Label>
          <PasswordInput
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
            disabled={loading}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="reset-confirm">
          <Form.Label>Confirm new password</Form.Label>
          <PasswordInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            required
            disabled={loading}
          />
        </Form.Group>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-3">
            <Button variant="link" className="p-0" onClick={onBack} disabled={loading}>
              ← Back to sign in
            </Button>
            <Button
              variant="link"
              className="p-0"
              onClick={handleResendCode}
              disabled={loading || !email.trim()}
            >
              Resend code
            </Button>
          </div>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" className="me-2" /> : null}
            Reset Password
          </Button>
        </div>
      </Form>
    );
  }

  return (
    <Form onSubmit={handleRequestCode}>
      {error && <Alert variant="danger">{error}</Alert>}
      <p className="text-muted small">
        Enter the email address associated with your account. We'll send you a 6-digit code to reset your password.
      </p>
      <Form.Group className="mb-3" controlId="forgot-email">
        <Form.Label>Email address</Form.Label>
        <Form.Control
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          disabled={loading}
        />
      </Form.Group>
      <div className="d-flex justify-content-between align-items-center">
        <Button variant="link" className="p-0" onClick={onBack}>
          ← Back to sign in
        </Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" className="me-2" /> : null}
          Send Code
        </Button>
      </div>
    </Form>
  );
}

// ── Confirm-account form (6-digit code) ───────────────────────────────────────

function ConfirmAccountForm({
  username,
  onDone,
}: {
  username: string;
  onDone: () => void;
}) {
  const dispatch = useAppDispatch();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Please enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    try {
      await authService.confirmAccount(username, code.trim());
      dispatch(emailConfirmed());
      setDone(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <>
        <Alert variant="success">
          Your email has been confirmed. Thanks!
        </Alert>
        <Button variant="primary" onClick={onDone}>
          Close
        </Button>
      </>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger">{error}</Alert>}
      <p className="text-muted small">
        We've sent a 6-digit confirmation code to the email address for <strong>{username}</strong>.
        Enter it below to confirm your account.
      </p>
      <Form.Group className="mb-3" controlId="confirm-code">
        <Form.Label>6-digit code</Form.Label>
        <Form.Control
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="123456"
          required
          disabled={loading}
          autoFocus
        />
      </Form.Group>
      <div className="d-flex justify-content-end">
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" className="me-2" /> : null}
          Confirm Account
        </Button>
      </div>
    </Form>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────────────

export default function AuthModal({ show, onHide, message, initialTab = 'login' }: Props) {
  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);
  const [registeredUser, setRegisteredUser] = useState<string | null>(null);

  // Keep activeTab in sync if the modal is reopened with a different initialTab
  React.useEffect(() => {
    if (show) setActiveTab(initialTab);
  }, [show, initialTab]);

  function handleRegistered(username: string) {
    setRegisteredUser(username);
  }

  function handleHide() {
    setActiveTab(initialTab);
    setRegisteredUser(null);
    onHide();
  }

  const confirmUsername = registeredUser ?? authService.getStoredUsername();
  const showConfirmView = registeredUser !== null || activeTab === 'confirm';

  return (
    <Modal show={show} onHide={handleHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {activeTab === 'forgot'
            ? 'Reset Password'
            : showConfirmView
              ? 'Confirm your account'
              : 'Account'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message && !showConfirmView && (
          <Alert variant="info" className="mb-3">{message}</Alert>
        )}
        {showConfirmView ? (
          confirmUsername ? (
            <ConfirmAccountForm username={confirmUsername} onDone={handleHide} />
          ) : (
            <Alert variant="warning">
              We couldn't determine which account to confirm. Please sign in first.
              <div className="mt-2">
                <Button variant="outline-primary" size="sm" onClick={() => setActiveTab('login')}>
                  Sign in
                </Button>
              </div>
            </Alert>
          )
        ) : activeTab === 'forgot' ? (
          <ForgotPasswordForm onBack={() => setActiveTab('login')} onDone={handleHide} />
        ) : (
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab((k as ActiveTab) ?? 'login')}
            className="mb-3"
          >
            <Tab eventKey="login" title="Sign In">
              <LoginForm onSuccess={handleHide} />
              <div className="mt-3 text-center">
                <Button
                  variant="link"
                  className="p-0 small"
                  onClick={() => setActiveTab('forgot')}
                >
                  Forgot your password?
                </Button>
              </div>
            </Tab>
            <Tab eventKey="register" title="Register">
              <RegisterForm onRegistered={handleRegistered} />
            </Tab>
          </Tabs>
        )}
      </Modal.Body>
    </Modal>
  );
}
